import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import Note from '@/components/Forms/Note';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { selectTeam } from '@/selectors';
import {
  PaginatedResponse,
  TopVideosResource,
  UnassignedVideoResource,
  VideoResource,
  VideoTopicResource,
} from '@/types/resources';
import { api } from '@/utils/api';
import { TopicsLevel } from '@/utils/enums';
import { RootState } from '@/utils/store';

import { AttachmentModalState } from './AssignTopicsTableRow';
import AttachmentsField from './AttachmentsField';

const StyledNote = styled(Note)`
  margin-bottom: 24px;
`;

interface Props {
  onClose: () => void;
  video: UnassignedVideoResource | VideoResource | TopVideosResource;
  videosQueryKey: Array<any>;
  attachmentModalState: AttachmentModalState;
}

export type FormData = {
  attachments: Omit<VideoTopicResource, 'id' | 'category'>[];
};

const AttachmentModal: FC<Props> = ({
  video,
  onClose,
  videosQueryKey,
  attachmentModalState,
}) => {
  const queryClient = useQueryClient();

  const { teamId } = useParams();

  const alerts = useAlerts();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      attachments: video.topics?.filter(
        (topic) => topic.category === attachmentModalState.category,
      ),
    },
  });
  useEffect(() => {
    register('attachments');
  }, [register]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const updatedVideo = await api.post<typeof data, UnassignedVideoResource>(
        `/v1/videos/${video.id}/topics`,
        {
          team_id: teamId === 'all' ? null : teamId,
          category: attachmentModalState.category,
          topics_status: null,
          topics: data.attachments.map((attachment) => {
            return {
              title: attachment.title,
              level: attachment.level,
            };
          }),
        },
      );

      const queryData =
        queryClient.getQueryData<
          InfiniteData<
            PaginatedResponse<
              UnassignedVideoResource | VideoResource | TopVideosResource
            >
          >
        >(videosQueryKey)!;

      const newPages = queryData.pages.map((page) => ({
        ...page,
        data: page.data.map((v) =>
          updatedVideo.id === v.id
            ? {
                ...v,
                topics: updatedVideo['topics'],
              }
            : v,
        ),
      }));

      const newQueryData = {
        ...queryData,
        pages: newPages,
      };

      queryClient.setQueryData(videosQueryKey, newQueryData);
      onClose();
      alerts.success(`Video topic have been successfully saved`);
    } catch (err) {
      alerts.error();
    }
  });

  const scriptTopicsNote = useSelector(
    (rootState: RootState) =>
      selectTeam(rootState, teamId!)?.preferences?.script_topics_note,
  );

  return (
    <Modal>
      <SidePanel
        heading={`Assign ${
          attachmentModalState.category === 'type'
            ? 'video types'
            : `${attachmentModalState.category} topics`
        }`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            {!!scriptTopicsNote &&
              attachmentModalState.category === 'script' && (
                <StyledNote textContent={scriptTopicsNote} />
              )}
            <AttachmentsField
              control={control}
              setValue={setValue}
              videoId={video.id}
              category={attachmentModalState.category}
              suggestionsEndpoint={`/v1/video-topics?category=${attachmentModalState.category}`}
              placeholder={`Add ${
                attachmentModalState.category === 'type' ? 'types' : 'topics'
              } and press enter`}
              label={
                attachmentModalState.category === 'script' ? 'Genre-level' : ''
              }
            />
            {attachmentModalState.category === 'script' && (
              <>
                <AttachmentsField
                  control={control}
                  setValue={setValue}
                  videoId={video.id}
                  category={attachmentModalState.category}
                  level={TopicsLevel.SUBGENRE}
                  suggestionsEndpoint={`/v1/video-topics?category=${attachmentModalState.category}`}
                  placeholder="Add topic and press enter"
                  label="Subgenre-level"
                />
                <AttachmentsField
                  control={control}
                  setValue={setValue}
                  videoId={video.id}
                  category={attachmentModalState.category}
                  level={TopicsLevel.OBJECTS}
                  suggestionsEndpoint={`/v1/video-topics?category=${attachmentModalState.category}`}
                  placeholder="Add topic and press enter"
                  label="Object-level"
                />
                <AttachmentsField
                  control={control}
                  setValue={setValue}
                  videoId={video.id}
                  category={attachmentModalState.category}
                  level={TopicsLevel.CHARACTERS}
                  suggestionsEndpoint={`/v1/video-topics?category=${attachmentModalState.category}`}
                  placeholder="Add topic and press enter"
                  label="Character-level"
                />
                <AttachmentsField
                  control={control}
                  setValue={setValue}
                  videoId={video.id}
                  category={attachmentModalState.category}
                  level={TopicsLevel.LOCATIONS}
                  suggestionsEndpoint={`/v1/video-topics?category=${attachmentModalState.category}`}
                  placeholder="Add topic and press enter"
                  label="Location-level"
                />
              </>
            )}
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default AttachmentModal;
